import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, Briefcase, User, Mail, Lock, Phone, Eye, EyeOff, ArrowRight } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getVendorTeamInviteInfo, acceptEmployeeInvite, register, login, preAcceptEmployeeInvite, switchRole, forgotPassword } from '../services/api';

function VendorInviteAccept() {
  const { token } = useParams();
  const { user, loginUser, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [preAccepting, setPreAccepting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [authMode, setAuthMode] = useState('register');
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  // Login hit an account created with Google (server code GOOGLE_ACCOUNT)
  const [googleAccount, setGoogleAccount] = useState(false);
  const [resetState, setResetState] = useState(''); // '', 'sending', 'sent'

  useEffect(() => {
    getVendorTeamInviteInfo(token)
      .then((res) => {
        setInviteInfo(res.data);
        if (res.data.invitedEmail) {
          setFormData(prev => ({ ...prev, email: res.data.invitedEmail }));
        }
        if (res.data.userExists) {
          setAuthMode('login');
        }
        // If already pending_signup or accepted, skip the info step
        if (res.data.inviteStatus === 'pending_signup' || res.data.inviteStatus === 'accepted') {
          setStatus('login-required');
        } else {
          setStatus('invite-info');
        }
      })
      .catch((err) => {
        // If already accepted, show success
        if (err.response?.data?.error?.includes('already accepted')) {
          setStatus('success');
          setResult({});
        } else if (err.response?.data?.error?.includes('revoked')) {
          setStatus('error');
          setError('This invitation was cancelled by the vendor. Ask them to send you a new one.');
        } else {
          setStatus('error');
          setError('Invalid or expired invitation link.');
        }
      });
  }, [token]);

  // Signed in as someone other than the invitee — typically the vendor owner
  // opening the link they just sent. Accepting would attach the invite to the
  // wrong account, so ask them to sign out instead of accepting automatically.
  const signedInAsOther = !!(user && inviteInfo?.invitedEmail
    && (user.email || '').toLowerCase() !== inviteInfo.invitedEmail.toLowerCase());

  useEffect(() => {
    if (!user || ['success', 'loading', 'error'].includes(status) || signedInAsOther) return;
    acceptEmployeeInvite(token)
      .then(async (res) => {
        setResult(res.data);
        setStatus('success');
        // A session opened as a couple/helper is still on that dashboard; move
        // it to the team one so "View My Projects" works without signing in again.
        if (user.role !== 'vendor_employee') {
          try {
            const sw = await switchRole('vendor_employee');
            loginUser(sw.data.user, sw.data.token);
          } catch { /* they can still switch from the account menu */ }
        }
      })
      .catch((err) => {
        const errorMsg = err.response?.data?.error || 'Failed to accept invitation';
        // If already accepted, that's actually success
        if (errorMsg.includes('already accepted')) {
          setStatus('success');
          setResult({});
        } else {
          setError(errorMsg);
          setStatus('error');
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, status, signedInAsOther]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setFormError('');
    const { firstName, lastName, email, phone, password, confirmPassword } = formData;
    if (!firstName || !lastName || !email || !password) {
      setFormError('Please fill in all required fields.');
      return;
    }
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }
    setFormLoading(true);
    try {
      const res = await register({ firstName, lastName, email, phone, password, role: 'vendor_employee', inviteToken: token });
      // Registering with the invite token already joins the team, so go
      // straight to the success screen rather than accepting a second time.
      setResult({ vendorName: inviteInfo?.vendorName, jobTitle: inviteInfo?.jobTitle });
      setStatus('success');
      loginUser(res.data.user, res.data.token);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Registration failed. Please try again.');
      setFormLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setFormError('');
    const { email, password } = formData;
    if (!email || !password) {
      setFormError('Please enter your email and password.');
      return;
    }
    setFormLoading(true);
    try {
      const res = await login({ email, password, inviteToken: token });
      loginUser(res.data.user, res.data.token);
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'GOOGLE_ACCOUNT') {
        // Google sign-in is couples-only and browser-only, so a team invite
        // never says "use Google": setting a password works everywhere.
        setGoogleAccount(true);
        setFormError(t('login.googleAccountNoGoogle'));
      } else {
        setFormError(data?.error || 'Login failed. Please try again.');
      }
      setFormLoading(false);
    }
  };

  const sendReset = async () => {
    if (!formData.email) {
      setFormError(t('login.enterEmailFirst'));
      return;
    }
    setResetState('sending');
    try {
      await forgotPassword(formData.email);
    } catch {
      // Same answer either way, as on /login, so emails can't be probed
    }
    setResetState('sent');
  };

  const handlePreAccept = async () => {
    setPreAccepting(true);
    try {
      await preAcceptEmployeeInvite(token);
    } catch (err) {
      // If already pending_signup, that's fine — proceed
      const msg = err.response?.data?.error || '';
      if (!msg.includes('accepted') && !msg.includes('pending_signup')) {
        setError(msg || 'Something went wrong. Please try again.');
        setStatus('error');
        return;
      }
    }
    setStatus('login-required');
    setPreAccepting(false);
  };

  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#374151' };

  if (status === 'loading') {
    return (
      <div className="invite-page">
        <div className="container" style={{ textAlign: 'center', paddingTop: '80px' }}>
          <p style={{ color: '#6b7280' }}>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (signedInAsOther && status !== 'success' && status !== 'error') {
    return (
      <div className="invite-page">
        <div className="container">
          <div className="invite-card" style={{ maxWidth: '480px', textAlign: 'center' }}>
            <Briefcase size={48} style={{ color: '#be185d', marginBottom: '16px' }} />
            <h2 style={{ margin: '0 0 8px', fontSize: '1.4rem', color: '#1a1a2e' }}>This invitation is for someone else</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '14px' }}>
              <strong>{inviteInfo.vendorName}</strong> sent it to <strong>{inviteInfo.invitedEmail}</strong>,
              but you're signed in as <strong>{user.email}</strong>. Sign out so the invited person can accept it.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button className="btn btn-primary" onClick={logout}
                style={{ padding: '12px 24px', fontSize: '15px', display: 'flex', justifyContent: 'center' }}>
                Sign out and continue
              </button>
              <button className="btn btn-outline" onClick={() => navigate('/')}
                style={{ padding: '10px 24px', fontSize: '14px', display: 'flex', justifyContent: 'center' }}>
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'invite-info' && inviteInfo) {
    return (
      <div className="invite-page">
        <div className="container">
          <div className="invite-card" style={{ maxWidth: '480px', textAlign: 'center' }}>
            <Briefcase size={48} style={{ color: '#be185d', marginBottom: '16px' }} />
            <h2 style={{ margin: '0 0 8px', fontSize: '1.4rem', color: '#1a1a2e' }}>{t('vendorTeam.inviteHeading')}</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '14px' }}>
              <strong>{inviteInfo.vendorName}</strong> has invited you
              {inviteInfo.jobTitle ? ` as a ${inviteInfo.jobTitle}` : ' to their team'}.
            </p>
            <div style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>{t('vendorTeam.inviteCompany')}</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{inviteInfo.vendorName}</span>
              </div>
              {inviteInfo.jobTitle && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>Role</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{inviteInfo.jobTitle}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>{t('vendorTeam.inviteEmail')}</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{inviteInfo.invitedEmail}</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button className="btn btn-primary" disabled={preAccepting} onClick={handlePreAccept}
                style={{ padding: '12px 24px', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <ArrowRight size={16} /> {preAccepting ? t('common.loading') : t('vendorTeam.acceptInvitation')}
              </button>
              <button className="btn btn-outline" onClick={() => navigate('/')}
                style={{ padding: '10px 24px', fontSize: '14px' }}>
                {t('vendorTeam.decline')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    const joinedName = result?.vendorName || inviteInfo?.vendorName;
    const joinedTitle = result?.jobTitle || inviteInfo?.jobTitle;
    return (
      <div className="invite-page">
        <div className="container">
          <div className="invite-card success">
            <CheckCircle size={48} className="text-green" />
            <h2>{t('vendorTeam.successHeading')} 🎉</h2>
            {joinedName ? (
              <p>
                You've joined <strong>{joinedName}</strong>
                {joinedTitle ? ` as a ${joinedTitle}` : ''}.
              </p>
            ) : (
              <p>This invitation has already been accepted.</p>
            )}
            <p className="invite-desc">
              {user ? 'Your assigned projects are ready to view.' : 'Sign in to view your assigned projects.'}
            </p>
            <div className="invite-actions">
              <button className="btn btn-primary" onClick={() => navigate('/my-projects')}>
                {t('vendorTeam.viewMyProjects')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="invite-page">
        <div className="container">
          <div className="invite-card error">
            <h2>Something went wrong</h2>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={() => navigate('/login')}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-page">
      <div className="container">
        <div className="invite-card" style={{ maxWidth: '480px', textAlign: 'left' }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <Briefcase size={40} style={{ color: '#be185d' }} />
            <h2 style={{ margin: '12px 0 4px' }}>{t('vendorTeam.invitePageTitle')}</h2>
            {inviteInfo ? (
              <p style={{ color: '#6b7280', fontSize: '14px' }}>
                {authMode === 'register'
                  ? `Create an account to join ${inviteInfo.vendorName}${inviteInfo.jobTitle ? ' as ' + inviteInfo.jobTitle : ''}`
                  : `Log in to join ${inviteInfo.vendorName}${inviteInfo.jobTitle ? ' as ' + inviteInfo.jobTitle : ''}`}
              </p>
            ) : (
              <p style={{ color: '#6b7280', fontSize: '14px' }}>
                {authMode === 'register' ? 'Create an account to accept this invitation' : 'Log in to accept this invitation'}
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #e5e7eb' }}>
            <button onClick={() => { setAuthMode('register'); setFormError(''); }}
              style={{ flex: 1, padding: '10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                color: authMode === 'register' ? '#be185d' : '#9ca3af',
                borderBottom: authMode === 'register' ? '2px solid #be185d' : '2px solid transparent', marginBottom: '-2px' }}>
              {t('vendorTeam.createAccountTab')}
            </button>
            <button onClick={() => { setAuthMode('login'); setFormError(''); }}
              style={{ flex: 1, padding: '10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                color: authMode === 'login' ? '#be185d' : '#9ca3af',
                borderBottom: authMode === 'login' ? '2px solid #be185d' : '2px solid transparent', marginBottom: '-2px' }}>
              {t('vendorTeam.loginTab')}
            </button>
          </div>

          {formError && resetState !== 'sent' && (
            <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
              {formError}
              {googleAccount && authMode === 'login' && (
                <div style={{ marginTop: '8px' }}>
                  <button type="button" onClick={sendReset} disabled={resetState === 'sending'}
                    style={{ background: 'none', border: 'none', color: '#be185d', fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                    {resetState === 'sending' ? t('login.sending') : t('login.setPassword')}
                  </button>
                </div>
              )}
            </div>
          )}

          {resetState === 'sent' && authMode === 'login' && (
            <div style={{ background: '#DCFCE7', color: '#16A34A', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
              {t('login.resetSent')} {t('acceptInvite.resetComeBack')}
            </div>
          )}

          {authMode === 'register' ? (
            <form onSubmit={handleRegister}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={labelStyle}>First Name</label>
                  <input style={inputStyle} placeholder="First name" value={formData.firstName}
                    onChange={e => setFormData(p => ({ ...p, firstName: e.target.value }))} />
                </div>
                <div>
                  <label style={labelStyle}>Last Name</label>
                  <input style={inputStyle} placeholder="Last name" value={formData.lastName}
                    onChange={e => setFormData(p => ({ ...p, lastName: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Email</label>
                <div style={{ position: 'relative' }}>
                  <Mail size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                  <input style={{ ...inputStyle, paddingLeft: '34px' }} type="email" placeholder="you@example.com" value={formData.email}
                    onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Phone (optional)</label>
                <div style={{ position: 'relative' }}>
                  <Phone size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                  <input style={{ ...inputStyle, paddingLeft: '34px' }} type="tel" placeholder="98XXXXXXXX" value={formData.phone}
                    onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                  <input style={{ ...inputStyle, paddingLeft: '34px', paddingRight: '38px' }} type={showPassword ? 'text' : 'password'} placeholder="Min. 6 characters" value={formData.password}
                    onChange={e => setFormData(p => ({ ...p, password: e.target.value }))} />
                  <button type="button" onClick={() => setShowPassword(v => !v)}
                    style={{ position: 'absolute', right: '8px', top: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px' }}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                  <input style={{ ...inputStyle, paddingLeft: '34px' }} type={showPassword ? 'text' : 'password'} placeholder="Re-enter password" value={formData.confirmPassword}
                    onChange={e => setFormData(p => ({ ...p, confirmPassword: e.target.value }))} />
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={formLoading}
                style={{ width: '100%', padding: '12px', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <User size={16} /> {formLoading ? t('vendorTeam.creatingAccount') : t('vendorTeam.createAndAccept')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Email</label>
                <div style={{ position: 'relative' }}>
                  <Mail size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                  <input style={{ ...inputStyle, paddingLeft: '34px' }} type="email" placeholder="you@example.com" value={formData.email}
                    onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <label style={labelStyle}>Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                  <input style={{ ...inputStyle, paddingLeft: '34px', paddingRight: '38px' }} type={showPassword ? 'text' : 'password'} placeholder="Password" value={formData.password}
                    onChange={e => setFormData(p => ({ ...p, password: e.target.value }))} />
                  <button type="button" onClick={() => setShowPassword(v => !v)}
                    style={{ position: 'absolute', right: '8px', top: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px' }}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              {/* Sends the reset link to the email above; after setting a
                  password they come back to this invitation link. */}
              <div style={{ textAlign: 'right', marginBottom: '16px' }}>
                <button type="button" onClick={sendReset} disabled={resetState === 'sending'}
                  style={{ background: 'none', border: 'none', color: '#be185d', fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: '4px 8px' }}>
                  {t('login.forgotPassword')}
                </button>
              </div>
              <button type="submit" className="btn btn-primary" disabled={formLoading}
                style={{ width: '100%', padding: '12px', fontSize: '15px' }}>
                {formLoading ? t('vendorTeam.loggingIn') : t('vendorTeam.loginAndAccept')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default VendorInviteAccept;
